import { cloneDeep } from 'lodash';
import { memo } from 'react';
import EMPTY_CHART_BAR_BASIC from '@/assets/empty-chart/bar-basic.svg';
import EMPTY_CHART_HORIZONTAL_BAR_BASIC from '@/assets/empty-chart/bar-horizontal-basic.svg';
import EMPTY_CHART_HORIZONTAL_BAR_STACKED from '@/assets/empty-chart/bar-horizontal-stacked.svg';
import EMPTY_CHART_BAR_STACKED from '@/assets/empty-chart/bar-stacked.svg';
import EMPTY_CHART_LINE_BASIC from '@/assets/empty-chart/line-basic.svg';
import EMPTY_CHART_PIE_BASIC from '@/assets/empty-chart/pie-basic.svg';
import EMPTY_CHART_PIE_DOUGHNUT from '@/assets/empty-chart/pie-doughnut.svg';
import { BaseLayerView } from '@/framework';
import { useLocaleStore } from '@/i18n';
import { ChartType, IChartLayer, ILayerProps, IRenderLayerProps } from '@/version-types';
import { isChartTestMode } from '../utils/consts';
import { getEchartsOptions } from '../utils/core';
import Chart from './chart';
import './chart-layer.less';

export const checkChartIsEmpty = (layer: Pick<IChartLayer, 'data'>) => layer.data.chartData.length === 0;

const EMPTY_CHART_MAP: Record<ChartType, string> = {
    'bar-basic': EMPTY_CHART_BAR_BASIC,
    'bar-stacked': EMPTY_CHART_BAR_STACKED,
    'bar-horizontal-basic': EMPTY_CHART_HORIZONTAL_BAR_BASIC,
    'bar-horizontal-stacked': EMPTY_CHART_HORIZONTAL_BAR_STACKED,
    'line-basic': EMPTY_CHART_LINE_BASIC,
    'pie-basic': EMPTY_CHART_PIE_BASIC,
    'pie-doughnut': EMPTY_CHART_PIE_DOUGHNUT,
};

interface IRenderLayerPropsOfChart extends Omit<IRenderLayerProps, 'layer'> {
    // 由于IRenderLayerProps没做范型，导致类型不明确，这里做了适配，明确了具体的类型
    layer: IChartLayer;
}

export const chartInnerViewClassName = 'saas-editor__chart';

export const ChartLayerPureView: React.FC<{
    layer: Pick<IChartLayer, 'width' | 'height' | 'data' | '__runtime'>;
    renderer?: 'svg' | 'canvas';
    onRenderEnd?: () => void;
}> = ({ layer, renderer = 'canvas', onRenderEnd }) => {
    const { $t } = useLocaleStore();
    const { fontFamilyMap } = layer.__runtime ?? {};
    const resetFontFamily = (obj: any, fontFamilyMap: Record<string, string>) => {
        if (typeof obj === 'object') {
            for (const key in obj) {
                if (key === 'fontFamily') {
                    const subFontFamily = fontFamilyMap[obj.fontFamily];
                    if (subFontFamily) {
                        obj.fontFamily = subFontFamily;
                    }
                } else {
                    resetFontFamily(obj[key], fontFamilyMap);
                }
            }
        }
    };
    const layerData = cloneDeep(layer.data);
    if (fontFamilyMap) {
        resetFontFamily(layerData, fontFamilyMap);
    }
    if (checkChartIsEmpty(layer)) {
        return (
            <div
                className="empty-chart"
                style={{ '--w': `${Math.min(layer.width, layer.height)}px` } as any}
                ref={() => {
                    setTimeout(() => {
                        onRenderEnd?.();
                    }, 30);
                }}
            >
                <img src={EMPTY_CHART_MAP[layer.data.type]} alt="" />
                <span>{$t((v) => v.chartLayer.emptyChart)}</span>
            </div>
        );
    }
    const echartsOptions = getEchartsOptions(layerData, {
        type: layer.data.type,
        chartWidth: layer.width,
        chartHeight: layer.height,
    });
    return (
        <Chart
            width={layer.width}
            height={layer.height}
            options={echartsOptions}
            renderer={renderer}
            onRenderEnd={onRenderEnd}
        />
    );
};

ChartLayerPureView.displayName = 'ChartLayerPureView';

const ChartLayer: React.FC<IRenderLayerPropsOfChart> = ({ identity: frameworkIdentity, layer, isNavigator }) => {
    return (
        <BaseLayerView layer={layer as ILayerProps} isNavigator={isNavigator}>
            <div
                className={chartInnerViewClassName}
                style={{ opacity: layer.opacity, pointerEvents: isChartTestMode ? 'initial' : undefined }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <ChartLayerPureView layer={layer} renderer={isChartTestMode ? 'svg' : 'canvas'} />
                </div>
            </div>
        </BaseLayerView>
    );
};

ChartLayer.displayName = 'ChartLayer';

export default memo(ChartLayer);
